import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BridgeCommandsService } from '@principle-theorem/ng-principle-bridge-cloud';
import {
  BridgeDeviceActionsService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { BridgeDevice, Practice } from '@principle-theorem/principle-core';
import {
  type IBridgeDevice,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  HISTORY_DATE_FORMAT,
  addDoc,
  doc,
  patchDoc,
  snapshot,
  snapshotDefined,
  type WithRef,
} from '@principle-theorem/shared';
import { kebabCase } from 'lodash';
import { ReplaySubject, type Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  EditBridgeDeviceDialogComponent,
  type EditBridgeDeviceFormData,
  type IEditBridgeDeviceRequest,
} from './edit-bridge-device-dialog/edit-bridge-device-dialog.component';
import {
  EditBridgeSettingsDialogComponent,
  IEditBridgeSettingsDialogData,
  IEditBridgeSettingsDialogResult,
} from '@principle-theorem/ng-principle-shared';
import {
  IViewBridgeLogsDialogData,
  ViewBridgeLogsDialogComponent,
} from './view-bridge-logs-dialog/view-bridge-logs-dialog.component';

@Component({
  selector: 'pr-practice-bridge-settings',
  templateUrl: './practice-bridge-settings.component.html',
  styleUrls: ['./practice-bridge-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PracticeBridgeSettingsComponent {
  private _bridgeActions = inject(BridgeDeviceActionsService);

  trackByDevice = TrackByFunctions.ref<WithRef<IBridgeDevice>>();
  practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  devices$: Observable<WithRef<IBridgeDevice>[]>;
  hasDownloadLink$: Observable<boolean>;
  readonly dateFormat = HISTORY_DATE_FORMAT;

  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this.practice$.next(practice);
    }
  }

  constructor(
    private _basicDialog: BasicDialogService,
    private _clipboard: Clipboard,
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService,
    private _bridgeCommands: BridgeCommandsService
  ) {
    this.devices$ = this.practice$.pipe(
      switchMap((practice) => Practice.bridgeDevices$(practice))
    );
  }

  async addNewDevice(): Promise<void> {
    const device = await this._openDeviceEdit({
      title: 'Add Bridge Device',
    });
    if (!device) {
      return;
    }
    const organisation = await snapshotDefined(
      this._organisation.organisation$
    );
    const practice = await snapshot(this.practice$);
    const docRef = doc(Practice.bridgeDeviceCol(practice));
    const deviceId = `${organisation.slug}-${kebabCase(device.name)}-${
      docRef.id
    }`;

    await addDoc(
      Practice.bridgeDeviceCol(practice),
      BridgeDevice.init({ ...device, deviceId })
    );
  }

  async editDevice(existing: WithRef<IBridgeDevice>): Promise<void> {
    const device = await this._openDeviceEdit({
      title: 'Edit Bridge Device',
      formData: existing,
    });
    if (!device) {
      return;
    }
    await patchDoc(existing.ref, device);
  }

  viewLogs(device: WithRef<IBridgeDevice>): void {
    this._basicDialog.open<
      ViewBridgeLogsDialogComponent,
      IViewBridgeLogsDialogData
    >(
      ViewBridgeLogsDialogComponent,
      DialogPresets.large({
        data: {
          device,
        },
      })
    );
  }

  async deleteDevice(device: WithRef<IBridgeDevice>): Promise<void> {
    await this._bridgeActions.deleteDevice(device.ref, device.name);
  }

  async generateIntegrationKey(device: WithRef<IBridgeDevice>): Promise<void> {
    const practice = await snapshot(this.practice$);
    this._snackBar.open('Generating Integration Key', 'Dismiss', {
      duration: 10000,
    });

    try {
      await this._bridgeActions.generateIntegrationKey({
        deviceRef: device.ref,
        deviceName: device.name,
        deviceId: device.deviceId ?? '',
        hasIntegrationKey: !!device.integrationKey,
        practiceRef: practice.ref,
      });
    } finally {
      this._snackBar.dismiss();
    }
  }

  async generateDownloadLink(device: WithRef<IBridgeDevice>): Promise<void> {
    const practice = await snapshot(this.practice$);
    await this._bridgeActions.generateDownloadLink({
      deviceRef: device.ref,
      deviceName: device.name,
      deviceId: device.deviceId ?? '',
      hasIntegrationKey: !!device.integrationKey,
      practiceRef: practice.ref,
    });
  }

  async updatedDeviceSettings(device: WithRef<IBridgeDevice>): Promise<void> {
    if (!device.settings) {
      return;
    }

    const updatedSettings = await this._basicDialog
      .open<
        EditBridgeSettingsDialogComponent,
        IEditBridgeSettingsDialogData,
        IEditBridgeSettingsDialogResult
      >(
        EditBridgeSettingsDialogComponent,
        DialogPresets.large({
          data: {
            settings: device.settings,
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (!updatedSettings) {
      return;
    }

    await this._bridgeCommands.sendSettings(device, updatedSettings.settings);
  }

  copy(deviceId: string): void {
    this._snackBar.open('Copied to clipboard');
    this._clipboard.copy(deviceId);
  }

  download(url?: string): void {
    if (url) {
      window.open(url);
    }
  }

  private async _openDeviceEdit(
    data: IEditBridgeDeviceRequest
  ): Promise<EditBridgeDeviceFormData | undefined> {
    return this._basicDialog
      .open<
        EditBridgeDeviceDialogComponent,
        IEditBridgeDeviceRequest,
        EditBridgeDeviceFormData
      >(EditBridgeDeviceDialogComponent, DialogPresets.medium({ data }))
      .afterClosed()
      .toPromise();
  }
}
